import React, { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { PrePopulatedSubscriptionData } from 'types/business'
import type { LineItems, ProductData } from 'types/summary'
import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'
import { updatePricingForm } from '@disco/state-management/actions/FormActions'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import api from '@disco/api/server'
import { PLAN_UPDATE_LOAD } from '@disco/constants/ActionTypes'
import type { BillingProviderComponentsState } from '@disco/state-management/reducers/billing-provider-components'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  getNumberOfUsers,
  getPrePopulatedSubscriptionData,
} from '@disco/state-management/selectors/user'
import { getSummary } from '@disco/utils/chargebee'
import type { BillingRootPath } from '@disco/utils/pricing'
import { UNLIMITED_TRACKS_QUOTA } from '@disco/utils/pricing'
import { ChargebeeForm } from './ChargebeeForm'
import type { RootState } from 'store/configureStore'
type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  onRequestCloseUpdateModal: (...args: Array<any>) => any
  onRequestClose: (...args: Array<any>) => any
  period: string
  setUpdateModal: (...args: Array<any>) => any
  tracksQuantity: number
  usersQuantity: number
  discoverySuiteQuantity: number
  watermarkingQuantity: number
  isChosenPlan: boolean
  defaultCountFiles: number
  defaultCountUser: number
  billingRef: {
    current: HTMLElement | null | undefined
  }
  rootPath: BillingRootPath
  showAllMobileContent: boolean
  showBillingSummary?: boolean
}

const ChargebeeFormContainer = ({
  onRequestCloseUpdateModal,
  onRequestClose,
  setUpdateModal,
  tracksQuantity = 0,
  usersQuantity = 0,
  discoverySuiteQuantity = 0,
  watermarkingQuantity = 0,
  isChosenPlan,
  defaultCountFiles,
  defaultCountUser,
  billingRef,
  updatePricingForm,
  pricingForm,
  billingProviderComponents,
  rootPath = '/settings/billing',
  isPhone,
  showAllMobileContent,
  prePopulatedSubscriptionData,
  showBillingSummary,
}: Props) => {
  const { plan: originalPlan, paymentPeriod: period } = pricingForm
  const plan = originalPlan || null
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [summary, setSummary]: [
    {
      loading: boolean
      loaded: boolean
      line_items?: LineItems[]
      total_in_cents: number
      coupon_code: string | null | undefined
    },
    (...args: Array<any>) => any,
  ] = useState({
    loading: false,
    loaded: false,
    coupon_code: null,
    total_in_cents: 0,
  })
  const [couponCode, setCouponCode] = useState(
    prePopulatedSubscriptionData.coupon_code || null,
  )
  const [productData, setProductData]: [
    ProductData,
    (...args: Array<any>) => any,
  ] = useState({
    product_handle: `${plan?.handle || ''}-${period}`,
    components: [],
    coupon_code: couponCode,
  })
  const [isOverQuota, setIsOverQuota] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState(null)
  const user = useSelector(state => state.user)
  const numberOfUsers = useSelector(getNumberOfUsers)

  const createSubscription = async (data): Promise<any> => {
    if (plan) {
      return api.chargebeePerformSignup(data.subscription)
    }
  }

  const loadSummary = async () => {
    if (!plan) return
    setSummary({ ...summary, loaded: false, loading: true })
    const newSummary = await getSummary({
      billingProviderComponents,
      couponCode,
      discoverySuiteQuantity,
      watermarkingQuantity,
      tracksQuantity,
      usersQuantity,
      setProductData,
      period,
      plan,
      productData,
      paymentDetails,
    })
    let newSummaryWithCoupon: {
      coupon_code?: string | null | undefined
    } = {}

    if (couponCode) {
      newSummaryWithCoupon.coupon_code = couponCode
    }

    setSummary({
      ...newSummary,
      ...newSummaryWithCoupon,
      loaded: true,
      loading: false,
    })
  }

  const handleSetCouponCode = code => {
    setCouponCode(code)
  }

  useEffect(() => {
    loadSummary()
  }, [
    couponCode,
    tracksQuantity,
    usersQuantity,
    discoverySuiteQuantity,
    watermarkingQuantity,
    pricingForm.plan?.handle,
    period,
    billingProviderComponents.loaded,
    paymentDetails,
  ])
  useEffect(() => {
    const total_track_count = user?.business?.stats?.total_track_count || 0
    const {
      componentsQuantity: { users, tracks },
      plan,
    } = pricingForm

    // Number() mirrors the implicit coercion `<` applied when users/tracks were untyped:
    // null compares as 0, undefined as NaN (always false)
    if (
      Number(users) < numberOfUsers ||
      (Number(tracks) < total_track_count && tracks !== UNLIMITED_TRACKS_QUOTA)
    ) {
      if (plan) {
        let path = `${rootPath}/over-quota-plan-modal?plan=${plan.name}`

        if (users) {
          path += `&users=${users}`
        }

        if (tracks === UNLIMITED_TRACKS_QUOTA) {
          path += `&tracks=unlimited`
        } else if (tracks) {
          path += `&tracks=${tracks}`
        }

        setIsOverQuota(true)
        navigate(path)
        return
      }
    }

    setIsOverQuota(false)
    // Only re-evaluate over-quota navigation when the quantities or plan change.
    // Depending on the whole `pricingForm` object causes navigation on unrelated
    // updates (e.g. `paymentDetailsSaved` toggled on every billing-info keystroke).
  }, [pricingForm.componentsQuantity, pricingForm.plan])
  useEffect(() => {
    if (!summary.loading && !summary.loaded) {
      loadSummary()
    }
  }, [dispatch])
  let calcTracksQuantity = tracksQuantity + 1000
  let calcUsersQuantity = usersQuantity + 1

  if (isChosenPlan) {
    calcTracksQuantity = tracksQuantity
    calcUsersQuantity = usersQuantity
  }

  if (!tracksQuantity) {
    calcTracksQuantity = defaultCountFiles
  }

  if (!usersQuantity) {
    calcUsersQuantity = defaultCountUser
  }

  if (!plan) return null
  const props = {
    createSubscription,
    onRequestCloseUpdateModal,
    onRequestClose,
    productData,
    applyCouponCode: handleSetCouponCode,
    setUpdateModal,
    summary,
    uploadPlan: async () => {
      await dispatch(loadBusinessSubscription())
      await dispatch(loadBillingProviderComponents())
      dispatch({
        type: PLAN_UPDATE_LOAD,
      })
    },
    user,
    tracksQuantity: calcTracksQuantity,
    usersQuantity: calcUsersQuantity,
    period,
    billingRef,
    updatePricingForm,
    plan,
    pricingForm,
    isOverQuota,
    setPaymentDetails,
    isPhone,
    showAllMobileContent,
    prePopulatedSubscriptionData,
    showBillingSummary,
  }

  // @ts-expect-error
  return <ChargebeeForm {...props} />
}

const mapStateToProps = (state: RootState) => ({
  pricingForm: state.forms.pricingForm,
  billingProviderComponents: state.billingProviderComponents,
  isPhone: isDeviceTypePhone(state),
  prePopulatedSubscriptionData: getPrePopulatedSubscriptionData(state),
})

const mapDispatchToProps = {
  updatePricingForm,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(ChargebeeFormContainer)
